import axios, {AxiosRequestConfig, AxiosResponse} from 'axios';
import {Buffer} from 'buffer';
import ApiConfig from './ApiConfig';

// Helper function to check if JWT is expired
const isTokenExpired = (token: string): boolean => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp * 1000; // Convert to milliseconds
        return Date.now() >= exp;
    } catch (error) {
        console.warn('Failed to parse JWT token:', error);
        return true; // Treat unparseable tokens as expired
    }
};

// Helper function to check if token needs refresh (within 5 minutes of expiry)
const shouldRefreshToken = (token: string): boolean => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp * 1000;
        const fiveMinutes = 5 * 60 * 1000;
        return (exp - Date.now()) < fiveMinutes;
    } catch (error) {
        return false;
    }
};

// Function to refresh Cognito token
const refreshCognitoToken = async (): Promise<string | null> => {
    try {
        const { fetchAuthSession } = await import('@aws-amplify/auth');
        const session = await fetchAuthSession({ forceRefresh: true });
        const newIdToken = session.tokens?.idToken?.toString();
        
        if (newIdToken) {
            localStorage.setItem('cognito_id_token', newIdToken);
            console.log('Cognito token refreshed successfully');
            return newIdToken;
        }
    } catch (error) {
        console.warn('Failed to refresh Cognito token:', error);
        // Clear expired tokens and redirect to login
        localStorage.removeItem('cognito_id_token');
        window.location.href = '/login';
    }
    return null;
};

// Add Axios interceptor to send JWT tokens (prioritize impersonation token)
axios.interceptors.request.use(async config => {
    // Check for impersonation token first, then fallback to Cognito token
    const impersonationToken = localStorage.getItem('impersonation_token');
    let cognitoToken = localStorage.getItem('cognito_id_token');
    
    let token = impersonationToken || cognitoToken;
    
    // Handle impersonation token expiration
    if (impersonationToken && isTokenExpired(impersonationToken)) {
        console.warn('Impersonation token expired, clearing from storage');
        localStorage.removeItem('impersonation_token');
        localStorage.removeItem('impersonated_user');
        token = cognitoToken; // Fall back to Cognito token
    }
    
    // Handle Cognito token refresh (sliding window)
    if (!impersonationToken && cognitoToken) {
        if (isTokenExpired(cognitoToken)) {
            console.warn('Cognito token expired, attempting refresh');
            const refreshedToken = await refreshCognitoToken();
            token = refreshedToken;
        } else if (shouldRefreshToken(cognitoToken)) {
            console.log('Cognito token needs refresh, refreshing proactively');
            const refreshedToken = await refreshCognitoToken();
            token = refreshedToken || cognitoToken; // Use refreshed or fall back to current
        }
    }
    
    if (token) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
});

// Add response interceptor to handle token expiration
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401 && error.response?.headers['x-auth-error'] === 'TOKEN_EXPIRED') {
            console.warn('Received TOKEN_EXPIRED response, clearing tokens and redirecting to login');
            
            // Clear all tokens
            localStorage.removeItem('cognito_id_token');
            localStorage.removeItem('impersonation_token');
            localStorage.removeItem('impersonated_user');
            
            // Redirect to login page
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export default class Api {
    private apiConfig: ApiConfig;

    constructor(apiConfig: ApiConfig) {
        this.apiConfig = apiConfig;
        this.apiConfig.platform = 'WEB';
        this.apiConfig.version = "2.3.8"
    }

    private _getBaseUrl(): string {
        return this.apiConfig.baseUrl;
    }

    private _getAxiosConfig(): AxiosRequestConfig {
        // axios will always lowercase the headers: https://github.com/axios/axios/issues/413
        const headers = {
            'jm-client': `${this.apiConfig.platform}-${this.apiConfig.version}`,
        };
        if (this.apiConfig.user) {
            // Only set Basic Auth if no JWT is present (neither impersonation nor cognito)
            const hasJwtToken = localStorage.getItem('impersonation_token') || localStorage.getItem('cognito_id_token');
            if (!hasJwtToken) {
                // @ts-ignore
                headers.Authorization = `Basic ${new Buffer(
                    `${this.apiConfig.user.email}:jm`,
                ).toString('base64')}`;
            }
        }
        return {
            headers: headers,
        };
    }

    public post<T = any>(url: string, data?: any): Promise<AxiosResponse<T>> {
        return axios.post(
            `${this._getBaseUrl()}${url}`,
            data,
            this._getAxiosConfig(),
        );
    }

    public get<T = any>(url: string): Promise<AxiosResponse<T>> {
        return axios.get(`${this._getBaseUrl()}${url}`, this._getAxiosConfig());
    }

    public head<T = any>(url: string): Promise<AxiosResponse<T>> {
        return axios.head(`${this._getBaseUrl()}${url}`, this._getAxiosConfig());
    }
}
